import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box } from '@mui/material';
import Navigation from './components/Navigation/Navigation';
import ChronicPrescriptionsHub from './pages/ChronicPrescriptions/ChronicPrescriptionsHub';
import PlanOfCareHub from './pages/PlanOfCareHub/PlanOfCareHub';
import Dashboard from './pages/Dashboard/Dashboard';
import Profile from './pages/Profile/Profile';
import PrivateHealthConsultantHub from './pages/PrivateHealthConsultantHub';
import PrivateHealthPracticeHub from './pages/PrivateHealthPracticeHub';
import Login from './pages/Login';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import UserDataTest from './components/UserDataTest';
import MedicationTest from './components/MedicationTest';
import PrescriptionTest from './components/PrescriptionTest';
import CarePlanBudgetTest from './components/CarePlanBudgetTest';

// Create a theme instance with a more modern style inspired by Revolut
const theme = createTheme({
  palette: {
    primary: {
      main: '#0066cc', // Slightly deeper blue
      light: '#e6f0ff',
      dark: '#004c99',
    },
    secondary: {
      main: '#00b8d4', // Teal accent
      light: '#e0f7fa',
      dark: '#008ba3',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#6b7280',
    },
    divider: 'rgba(0, 0, 0, 0.06)',
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.05)',
    '0px 8px 16px rgba(0, 0, 0, 0.05)',
    '0px 16px 24px rgba(0, 0, 0, 0.05)',
    '0px 24px 32px rgba(0, 0, 0, 0.05)',
    '0px 32px 40px rgba(0, 0, 0, 0.05)',
    '0px 40px 48px rgba(0, 0, 0, 0.05)',
    '0px 48px 56px rgba(0, 0, 0, 0.05)',
    '0px 56px 64px rgba(0, 0, 0, 0.05)',
    '0px 64px 72px rgba(0, 0, 0, 0.05)',
    '0px 72px 80px rgba(0, 0, 0, 0.05)',
    '0px 80px 88px rgba(0, 0, 0, 0.05)',
    '0px 88px 96px rgba(0, 0, 0, 0.05)',
    '0px 96px 104px rgba(0, 0, 0, 0.05)',
    '0px 104px 112px rgba(0, 0, 0, 0.05)',
    '0px 112px 120px rgba(0, 0, 0, 0.05)',
    '0px 120px 128px rgba(0, 0, 0, 0.05)',
    '0px 128px 136px rgba(0, 0, 0, 0.05)',
    '0px 136px 144px rgba(0, 0, 0, 0.05)',
    '0px 144px 152px rgba(0, 0, 0, 0.05)',
    '0px 152px 160px rgba(0, 0, 0, 0.05)',
    '0px 160px 168px rgba(0, 0, 0, 0.05)',
    '0px 168px 176px rgba(0, 0, 0, 0.05)',
    '0px 176px 184px rgba(0, 0, 0, 0.05)', // Adding shadow[24] to fix elevation warning
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 4,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.9rem',
        },
      },
    },
  },
});

// Main content component that conditionally renders the Navigation
const MainContent = () => {
  const { isAuthenticated } = useAuth();
  
  return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {isAuthenticated && <Navigation />}
          <Box 
            component="main" 
            sx={{ 
              flexGrow: 1, 
              p: { xs: 2, sm: 3 }, 
              pt: { xs: 1, sm: 1.5 },
          ml: isAuthenticated ? { md: '250px' } : 0, // Only add margin if authenticated
          width: isAuthenticated ? { md: 'calc(100% - 250px)' } : '100%' // Adjust width based on authentication
            }}
          >
            <Routes>
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/chronic-prescriptions" 
            element={
              <ProtectedRoute>
                <ChronicPrescriptionsHub />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/plan-of-care" 
            element={
              <ProtectedRoute>
                <PlanOfCareHub />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/private-health-consultant" 
            element={
              <ProtectedRoute>
                <PrivateHealthConsultantHub />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/private-health-practice" 
            element={
              <ProtectedRoute>
                <PrivateHealthPracticeHub />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } 
          />
          {/* API Test Route */}
          <Route 
            path="/api-test" 
            element={
              <ProtectedRoute>
                <UserDataTest />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/medication-test" 
            element={
              <ProtectedRoute>
                <MedicationTest />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/prescription-test" 
            element={
              <ProtectedRoute>
                <PrescriptionTest />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/careplan-test" 
            element={
              <ProtectedRoute>
                <CarePlanBudgetTest />
              </ProtectedRoute>
            } 
          />
            </Routes>
          </Box>
        </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <LanguageProvider>
          <Router>
            <MainContent />
          </Router>
        </LanguageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;